import React, { useEffect, useState } from "react";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

function renderValue(value) {
  if (value === null || value === undefined || value === "") return <em>—</em>;
  if (Array.isArray(value)) return value.map((v, i) => <Pill key={`${v}-${i}`}>{String(v)}</Pill>);
  return <span>{String(value)}</span>;
}

function ConflictCard({ conflict, onResolved }) {
  const [chosen, setChosen] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const stmts = conflict.hydrated_statements || [];

  const resolve = async (resolution) => {
    setErr(null); setBusy(true);
    try {
      const chosen_id = resolution === "resolved_choose" ? chosen : null;
      const actual = resolution === "resolved_choose" ? "resolved_choose_a" : resolution;
      await api.post(`/intelligence/conflicts/${conflict.id}/resolve`, {
        resolution: actual,
        chosen_statement_id: chosen_id,
        resolution_note: note || null,
      });
      onResolved();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bo-card" style={{ padding: 18, marginBottom: 12 }} data-testid={`cc-conflict-${conflict.id}`}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div className="bo-eyebrow">Conflict</div>
          <h2 style={{ margin: "4px 0 0 0", fontFamily: "var(--bo-mono)", fontSize: 15 }}>{conflict.field_path}</h2>
          <div style={{ fontSize: 12, color: "var(--bo-muted)", marginTop: 2 }}>{conflict.scope} · {stmts.length} competing statements</div>
        </div>
        <Pill tone="danger">OPEN</Pill>
      </div>
      <div style={{ marginTop: 12 }}>
        {stmts.map((s, i) => (
          <label key={s.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: 10,
            background: chosen === s.id ? "var(--bo-panel-alt)" : "transparent",
            border: chosen === s.id ? "1px solid var(--bo-accent)" : "1px solid var(--bo-line)",
            borderRadius: "var(--bo-radius-sm)", marginBottom: 8, cursor: "pointer" }}
            data-testid={`cc-option-${conflict.id}-${i}`}>
            <input type="radio" name={`conflict-${conflict.id}`} value={s.id} checked={chosen === s.id} onChange={() => setChosen(s.id)} />
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", gap: 6, marginBottom: 4 }}>
                <Pill tone="muted">{(s.confidence_factors || {}).authority || "—"}</Pill>
                <Pill tone="muted">{s.confidence_band || "—"}</Pill>
                <Pill tone="muted">v{s.version || 1}</Pill>
              </div>
              <div>{renderValue(s.value)}</div>
            </div>
          </label>
        ))}
      </div>
      <input className="bo-input" value={note} onChange={e => setNote(e.target.value)}
        placeholder="Resolution note (recommended for audit)" style={{ marginTop: 8 }} data-testid={`cc-note-${conflict.id}`} />
      {err && <div className="bo-login-error" role="alert" style={{ marginTop: 8 }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
        <button type="button" className="bo-btn bo-btn-primary" onClick={() => resolve("resolved_choose")}
          disabled={busy || !chosen} data-testid={`cc-choose-${conflict.id}`}>
          {busy ? "Resolving…" : "Choose selected → promote"}
        </button>
        <button type="button" className="bo-btn bo-btn-secondary" onClick={() => resolve("resolved_coexist")}
          disabled={busy} data-testid={`cc-coexist-${conflict.id}`}>Both coexist</button>
        <button type="button" className="bo-btn bo-btn-ghost" onClick={() => resolve("deferred")}
          disabled={busy} data-testid={`cc-defer-${conflict.id}`}>Defer</button>
      </div>
    </div>
  );
}

export default function ConflictCenter() {
  const [conflicts, setConflicts] = useState(null);
  const [error, setError] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [seedErr, setSeedErr] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/intelligence/conflicts?status=open&limit=100");
      setConflicts(r.data.conflicts || []);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const seedDemo = async () => {
    setSeedErr(null); setSeeding(true);
    try {
      await api.post("/intelligence/dev/seed-conflict", {});
      await load();
    } catch (e) {
      setSeedErr(formatApiError(e));
    } finally {
      setSeeding(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <Shell title="Conflict Center">
      <div className="bo-eyebrow" data-testid="cc-eyebrow">Gate 2 · Slice 3 · Conflict Resolution</div>
      <h1 className="bo-serif" style={{ fontSize: 34, lineHeight: 1.1, letterSpacing: "-0.02em", margin: "6px 0 8px" }} data-testid="cc-title">
        Conflict Center
      </h1>
      <p style={{ color: "var(--bo-muted)", maxWidth: 720, margin: "0 0 20px 0" }} data-testid="cc-lede">
        When two source-grounded statements disagree on the same field, the intelligence layer
        clusters them here for human resolution. Nothing auto-resolves.
      </p>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <button
          type="button"
          className="bo-btn bo-btn-ghost"
          onClick={seedDemo}
          disabled={seeding}
          data-testid="cc-seed-demo-btn"
          title="Owner-only: creates two disagreeing statements at demo.conflict_seed so you can walk through the resolve flow."
        >
          {seeding ? "Seeding…" : "Seed demo conflict"}
        </button>
        {seedErr && <span className="bo-login-error" role="alert" data-testid="cc-seed-error">{seedErr}</span>}
      </div>
      <ErrorBanner error={error} />
      {conflicts === null && <LoadingCard label="Loading conflicts…" />}
      {conflicts !== null && (
        <>
          <p style={{ marginBottom: 16, color: "var(--bo-muted)" }} data-testid="cc-count">
            {conflicts.length} open conflict{conflicts.length === 1 ? "" : "s"}
          </p>
          {conflicts.length === 0 ? (
            <EmptyState title="No open conflicts" description="Contested field values will appear here as soon as they're detected on write." testId="cc-empty" />
          ) : (
            conflicts.map(c => <ConflictCard key={c.id} conflict={c} onResolved={load} />)
          )}
        </>
      )}
    </Shell>
  );
}
